// The spoken agent in the app: ElevenLabs' Agents platform holds the
// conversation (ears, mouth, turn-taking, barge-in) and calls the server as
// its language model, which is AlfieOS's own assistant. What the agent can
// do on this screen it does through client tools, which land here and run
// through the same runner a typed turn's device calls use (commands.ts), so a spoken "pause" and
// a typed one are the same code, and a proposed change is applied through
// the ordinary write path where undo can reach it.
import { useEffect, type ReactNode } from 'react'
import { ConversationProvider, useConversation } from '@elevenlabs/react'
import { create } from 'zustand'
import { api, db, engine } from '@/runtime'
import { applyForAgent } from '@/lib/suggestions'
import type { SuggestionResource } from '@/kinds/suggestion'
import { listenInterim, voiceAvailable } from './listen'
import type { Command } from './commands'
import { sessionTranscript, voiceLog } from './log'
import { useVoicePrefs } from '@/state/voicePrefs'

/** What the rest of the app sees of the agent: the voice bar and the centre button read it. */
export interface AgentState {
  /** the server has a platform key and an agent */
  available: boolean
  status: 'disconnected' | 'connecting' | 'connected'
  speaking: boolean
  /** the last thing heard and the last thing said, for the bubble */
  heard: string
  said: string
  error: string | null
  /** microphone level 0..1 while connected, so a silent mic shows as silent */
  level: number
  /** what the agent is playing back, 0..1, so 'speaking but silent' is visible */
  outLevel: number
  /** the words as they are being said, from the browser's own recogniser (the platform only sends finished sentences) */
  interim: string
  /** the microphone is held shut: between turns in push-to-talk, or after "your turn" until the reply starts */
  muted: boolean
  /** "your turn" was tapped: the mic stays shut until the agent starts speaking */
  turnPending: boolean
  start: () => Promise<void>
  stop: () => void
  setMuted: (muted: boolean) => void
  /** open mic: I'm done talking. Shuts the mic so the platform hears the silence and answers now. */
  yourTurn: () => void
  /** type to the agent instead of speaking; the reply is still spoken */
  sendText: (text: string) => void
}

export const useAgentState = create<AgentState>(() => ({
  available: false,
  status: 'disconnected',
  speaking: false,
  heard: '',
  said: '',
  error: null,
  level: 0,
  outLevel: 0,
  interim: '',
  muted: false,
  turnPending: false,
  start: async () => {},
  stop: () => {},
  setMuted: () => {},
  yourTurn: () => {},
  sendText: () => {},
}))

// the store on the window for a throwaway screenshot spec to pose a
// conversation, since a real one cannot run headless; it holds nothing secret
if (typeof window !== 'undefined') (window as unknown as { __voice?: unknown }).__voice = useAgentState

const WORKLETS = {
  rawAudioProcessor: '/worklets/rawAudioProcessor.js',
  audioConcatProcessor: '/worklets/audioConcatProcessor.js',
}
const RESAMPLER = '/worklets/libsamplerate.worklet.js'

// The SDK's output side never receives `libsampleratePath` (its websocket
// setup passes the path to the input only), so on Safari, which needs the
// resampler for playback as well, it asks jsDelivr for the module and the
// policy refuses it. The one CDN address is routed to the copy this origin
// serves; every other module load is untouched.
const CDN_RESAMPLER =
  /^https:\/\/cdn\.jsdelivr\.net\/npm\/@alexanderolsen\/libsamplerate-js@[^/]+\/dist\/libsamplerate\.worklet\.js$/
if (typeof AudioWorklet !== 'undefined') {
  const addModule = AudioWorklet.prototype.addModule
  AudioWorklet.prototype.addModule = function (url: string | URL, options?: WorkletOptions) {
    const s = String(url)
    return addModule.call(this, CDN_RESAMPLER.test(s) ? RESAMPLER : url, options)
  }
}

// a policy refusal names only "Not allowed by CSP" in the SDK's error; the
// document's own event has the directive and the address, so that is what
// reaches the trail, for the one device where the policy is real: the phone
if (typeof document !== 'undefined') {
  document.addEventListener('securitypolicyviolation', (e) => {
    if (useAgentState.getState().status === 'disconnected') return
    voiceLog(
      'error',
      `csp: ${e.violatedDirective} refused ${e.blockedURI || '(inline)'} at ${e.sourceFile || '?'}:${e.lineNumber}`,
    )
  })
}

// the device's own recogniser for live words: one per call, module-level so
// the Talk tap can start it before the session's awaits and the hook can stop it
let liveWordsStop: (() => void) | null = null
const stopLiveWords = () => {
  liveWordsStop?.()
  liveWordsStop = null
  useAgentState.setState({ interim: '' })
}
const startLiveWords = () => {
  if (liveWordsStop || !useVoicePrefs.getState().liveWords || !voiceAvailable()) return
  let started = false
  liveWordsStop = listenInterim(
    (t) => {
      if (!useAgentState.getState().speaking) useAgentState.setState({ interim: t })
    },
    () => {
      if (!started) voiceLog('session', 'live words: recogniser started')
      started = true
    },
    (why) => voiceLog('error', `live words: ${why}`),
  )
}

export const VoiceAgentProvider = ({ children }: { children: ReactNode }) => (
  <ConversationProvider>{children}</ConversationProvider>
)

export interface AgentHandlers {
  /** run a command and say what happened; the words are the tool's answer */
  run: (c: Command) => Promise<string>
  /** work a Home Assistant entity by the name the user said */
  home: (name: string, service?: string) => Promise<string>
}

/** Apply suggestions the server persisted for this turn: sync, then the ordinary accept path. */
export const applySuggestionIds = async (ids: string[]): Promise<string> => {
  await engine.sync()
  let n = 0
  const missing: string[] = []
  const did: string[] = []
  for (const id of ids) {
    const s = (await db.resources.get(id)) as SuggestionResource | undefined
    if (s?.kind !== 'Suggestion') {
      missing.push(id)
      continue
    }
    n += await applyForAgent(s)
    did.push(`${s.spec.title} (${s.spec.ops.map((o) => `${o.type} ${o.kind ?? ''}`.trim()).join(', ')})`)
  }
  if (missing.length)
    voiceLog('error', `apply: ${missing.length} suggestion(s) not synced yet: ${missing.join(', ')}`)
  return n
    ? `applied ${n} change${n === 1 ? '' : 's'}: ${did.join('; ')}`
    : `nothing was applied${missing.length ? ' (the suggestion had not reached this device)' : ''}`
}

/** the Shell's handlers as of the last render; the tools read them when called */
const handlers: { current: AgentHandlers | null } = { current: null }
const live = (): AgentHandlers => {
  if (!handlers.current) throw new Error('the voice handlers are not mounted')
  return handlers.current
}

/** the SDK's audio worklets and resampler, served by the app (web/public/worklets) so the script policy allows them */

const asString = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v))

/**
 * The hook the Shell mounts once (`ShellVoice`). Client tools map onto commands; the session
 * is a signed URL from the server, so the platform key never reaches the
 * browser. `start` asks for the microphone first, because the SDK wants it
 * before the session opens.
 */
/** Every tool goes through here, so each call and its answer (or failure) is logged the same way. */
const logged =
  (name: string, fn: (p: Record<string, unknown>) => Promise<string>) =>
  async (p: Record<string, unknown>): Promise<string> => {
    voiceLog('tool', `${name} ${JSON.stringify(p)}`)
    try {
      const r = await fn(p)
      voiceLog('result', `${name}: ${r}`)
      return r
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      voiceLog('error', `${name}: ${msg}`)
      return `failed: ${msg}`
    }
  }

export const useVoiceAgent = (h: AgentHandlers) => {
  // the latest handlers, where the tool closures can read them at call time;
  // one Shell mounts one agent, so module state is the simplest honest box
  useEffect(() => {
    handlers.current = h
  })
  const conversation = useConversation({
    // the SDK's audio worklets, served from this origin: it would otherwise
    // load them from a blob URL and, where that fails (WebKit), a data: URL,
    // both outside the page's script policy
    workletPaths: WORKLETS,
    // the resampler, for browsers that cannot open the mic at 16 kHz (Safari, always):
    // the SDK would fetch it from jsDelivr, outside the policy
    libsampleratePath: RESAMPLER,
    clientTools: {
      capture: logged('capture', async (p: Record<string, unknown>) =>
        live().run({ kind: 'capture', text: asString(p.text) }),
      ),
      player: logged('player', async (p: Record<string, unknown>) => {
        const action = asString(p.action) as 'play' | 'pause' | 'next' | 'previous'
        const what = asString(p.what)
        const device = asString(p.device) || undefined
        const sort = asString(p.kind) as 'track' | 'album' | 'artist' | 'playlist' | ''
        if (action === 'play' && (what || device))
          return live().run({ kind: 'play', what, sort: sort || undefined, device })
        return live().run({ kind: 'transport', action: action || 'play' })
      }),
      focus: logged('focus', async (p: Record<string, unknown>) =>
        live().run({
          kind: 'pomodoro',
          action: (asString(p.action) || 'start') as 'start' | 'pause' | 'skip' | 'reset',
        }),
      ),
      open: logged('open', async (p: Record<string, unknown>) =>
        live().run({ kind: 'open', view: asString(p.view) }),
      ),
      task: logged('task', async (p: Record<string, unknown>) => {
        const action = asString(p.action)
        if (action === 'done') return live().run({ kind: 'done' })
        if (action === 'start') return live().run({ kind: 'start' })
        return live().run({ kind: 'whatnext' })
      }),
      home: logged('home', async (p: Record<string, unknown>) =>
        live().home(asString(p.name), asString(p.service) || undefined),
      ),
      hang_up: logged('hang_up', async () => {
        // let the goodbye finish playing, then close the line
        window.setTimeout(() => useAgentState.getState().stop(), 2500)
        return 'hanging up'
      }),
      apply_suggestions: logged('apply_suggestions', async (p: Record<string, unknown>) =>
        applySuggestionIds(Array.isArray(p.ids) ? p.ids.map(asString) : []),
      ),
    },
    onMessage: (m: { message: string; source: string }) => {
      // a new thing heard starts a new exchange: the old reply goes, the new one lands when it comes
      if (m.source === 'user') {
        voiceLog('heard', m.message)
        useAgentState.setState({ heard: m.message, said: '', interim: '' })
      } else {
        voiceLog('said', m.message)
        useAgentState.setState({ said: m.message })
      }
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e)
      voiceLog('error', msg)
      useAgentState.setState({ error: msg })
    },
    onConnect: () => voiceLog('session', 'connected'),
    onDisconnect: (d: unknown) => {
      voiceLog('session', `disconnected ${JSON.stringify(d ?? '')}`.slice(0, 200))
      // the platform forgets the conversation now; the server keeps a summary
      const transcript = sessionTranscript()
      if (transcript.some((t) => t.role === 'user')) void api.voiceSummarise(transcript).catch(() => {})
    },
  })
  const {
    status,
    isSpeaking,
    isMuted,
    setMuted,
    startSession,
    endSession,
    getInputVolume,
    getOutputVolume,
    sendUserMessage,
  } = conversation

  // is there an agent at all: the server says, once
  useEffect(() => {
    let stale = false
    api
      .config()
      .then((c) => {
        if (!stale) useAgentState.setState({ available: !!c.voice })
      })
      .catch(() => {})
    return () => {
      stale = true
    }
  }, [])

  // the input meter: what the microphone is actually feeding the session
  useEffect(() => {
    if (status !== 'connected') {
      useAgentState.setState({ level: 0 })
      return
    }
    const t = window.setInterval(() => {
      try {
        useAgentState.setState({
          level: Math.min(1, getInputVolume()),
          outLevel: Math.min(1, getOutputVolume()),
        })
      } catch {
        /* not ready yet */
      }
    }, 120)
    return () => window.clearInterval(t)
  }, [status, getInputVolume, getOutputVolume])

  // words as they are spoken: the platform transcribes whole sentences, so the
  // device's recogniser runs alongside, for display only. It is started in the
  // Talk tap (`start` below) and lives for the whole call; results are ignored
  // while the agent talks, and the switch stops or starts it mid-call
  const liveWords = useVoicePrefs((s) => s.liveWords)
  useEffect(() => {
    if (status === 'disconnected' || !liveWords) stopLiveWords()
    else if (status === 'connected') startLiveWords()
  }, [status, liveWords])
  useEffect(() => {
    if (isSpeaking) useAgentState.setState({ interim: '' })
  }, [isSpeaking])

  useEffect(() => {
    // the SDK's 'error' is a disconnect with a reason; the reason lands via onError
    useAgentState.setState({ status: status === 'error' ? 'disconnected' : status, speaking: isSpeaking })
  }, [status, isSpeaking])
  useEffect(() => {
    useAgentState.setState({ muted: isMuted })
  }, [isMuted])

  // "your turn": the mic shut until the reply begins, with a fuse in case the
  // platform answers nothing (a refusal, a dropped turn), so it never stays shut
  const turnPending = useAgentState((s) => s.turnPending)
  useEffect(() => {
    if (!turnPending) return
    if (status !== 'connected') {
      useAgentState.setState({ turnPending: false })
      return
    }
    const open = () => {
      useAgentState.setState({ turnPending: false })
      if (!useVoicePrefs.getState().pushToTalk) useAgentState.getState().setMuted(false)
    }
    if (isSpeaking) {
      open()
      return
    }
    const t = window.setTimeout(open, 12_000)
    return () => window.clearTimeout(t)
  }, [turnPending, isSpeaking, status])

  useEffect(() => {
    useAgentState.setState({
      start: async () => {
        useAgentState.setState({ error: null, heard: '', said: '' })
        voiceLog('session', 'start')
        // before any await: the recogniser wants the tap behind it on iOS
        startLiveWords()
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true })
          const s = await api.voiceSession()
          if (!s.configured || !s.signed_url) throw new Error('The spoken agent is not set up on the server.')
          startSession({
            signedUrl: s.signed_url,
            connectionType: 'websocket',
            // named here as well as on the hook: options given to startSession replace the
            // hook's defaults, and these must reach the SDK every time
            workletPaths: WORKLETS,
            libsampleratePath: RESAMPLER,
          })
        } catch (e) {
          stopLiveWords()
          voiceLog('error', `start: ${e instanceof Error ? e.message : String(e)}`)
          throw e
        }
      },
      stop: () => endSession(),
      // the SDK throws when there is no session (hang-up's cleanup, a posed state)
      setMuted: (m) => {
        try {
          setMuted(m)
        } catch {
          /* no session to mute */
        }
      },
      yourTurn: () => {
        try {
          setMuted(true)
        } catch {
          return
        }
        useAgentState.setState({ turnPending: true, interim: '' })
      },
      sendText: (text) => {
        useAgentState.setState({ heard: text })
        sendUserMessage(text)
      },
    })
  }, [startSession, endSession, sendUserMessage, setMuted])

  return useAgentState()
}
